import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { noop } from 'ui-components/default-props/noop';

const WizardContext = React.createContext({
  stepForward: noop,
  setCanProgress: noop,
});
export const useWizard = () => useContext(WizardContext);

class WizardStep {
  constructor({ stepId, prevStepId, nextStepId }) {
    this.stepId = stepId;
    this.prevStepId = prevStepId;
    this.nextStepId = nextStepId;
  }

  get hasPreviousStep() {
    return this.prevStepId !== undefined && this.prevStepId !== null;
  }

  get hasNextStep() {
    return this.nextStepId !== undefined && this.nextStepId !== null;
  }
}

export const WizardContextProvider = ({
  children,
  wizardStepIds,
  getStepLabel,
  onComplete = noop,
  allowInitialProgress = false,
  setCurrentStepId = noop,
  initialStepId,
}) => {
  // state
  const [canProgress, setCanProgress] = useState(allowInitialProgress);
  const [activeStepId, setActiveStepId] = useState(initialStepId ?? wizardStepIds?.[0]);

  // callbacks and memo
  const wizardSteps = useMemo(() => {
    return wizardStepIds.reduce((steps, stepId, index) => {
      return {
        ...steps,
        [stepId]: new WizardStep({
          stepId,
          prevStepId: wizardStepIds[index - 1],
          nextStepId: wizardStepIds[index + 1],
        }),
      };
    }, {});
  }, [wizardStepIds]);

  const activeStep = useMemo(() => {
    return wizardSteps[activeStepId];
  }, [activeStepId, wizardSteps]);

  const jumpToStep = useCallback(
    (stepId) => {
      if (wizardSteps[stepId]) {
        setCanProgress(false);
        setActiveStepId(stepId);
      }
    },
    [wizardSteps, setCanProgress, setActiveStepId],
  );

  const stepForward = useCallback(() => {
    if (canProgress && activeStep?.hasNextStep) {
      setCanProgress(false);
      setActiveStepId(activeStep.nextStepId);
    }
  }, [canProgress, activeStep, setCanProgress]);

  const stepBack = useCallback(() => {
    if (activeStep?.hasPreviousStep) {
      setCanProgress(false);
      setActiveStepId(activeStep.prevStepId);
    }
  }, [activeStep, setCanProgress]);

  const value = useMemo(
    () => ({
      activeStep,
      previousStep: activeStep?.hasPreviousStep ? wizardSteps[activeStep.prevStepId] : null,
      nextStep: activeStep?.hasNextStep ? wizardSteps[activeStep.nextStepId] : null,
      jumpToStep,
      stepForward,
      canProgress,
      setCanProgress,
      stepBack,
      getStepLabel,
      onComplete,
    }),
    [activeStep, jumpToStep, wizardSteps, stepForward, canProgress, setCanProgress, stepBack, getStepLabel, onComplete],
  );

  // effects
  useEffect(() => {
    setCurrentStepId(activeStepId);
  }, [activeStepId, setCurrentStepId]);

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

WizardContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  wizardStepIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  getStepLabel: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  setCurrentStepId: PropTypes.func,
  allowInitialProgress: PropTypes.bool,
  initialStepId: PropTypes.string,
};
